import { Component, OnInit, OnDestroy, input, output, inject, ChangeDetectorRef, ViewChild } from '@angular/core';
import { Observable, Subject, BehaviorSubject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError, takeUntil, map, tap, finalize } from 'rxjs/operators';
import { NgSelectModule, NgSelectComponent } from '@ng-select/ng-select';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DataGetRequest, DataGetFn } from '@controladad/ng-base';

declare const snackbar$: any;

// Generic interface for dropdown items
export interface LazyDropdownItem {
  id: number;
  label: string;
  value: number;
  subtitle?: string;
  [key: string]: any; // Allow additional properties
}

// Type for API adapter function (returns a DataGetFn like GetApiAdapter does)
export type ApiAdapterFunction<T> = DataGetFn<T>;

// Type for item mapper function
export type ItemMapperFunction<T> = (item: T) => LazyDropdownItem;

@Component({
  selector: 'app-lazy-dropdown',
  templateUrl: './lazy-dropdown.component.html',
  styleUrls: ['./lazy-dropdown.component.scss'],
  imports: [CommonModule, FormsModule, NgSelectModule],
})
export class LazyDropdownComponent<T = any> implements OnInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild(NgSelectComponent) ngSelect!: NgSelectComponent;

  // Required inputs
  apiAdapter = input.required<ApiAdapterFunction<T>>();
  itemMapper = input.required<ItemMapperFunction<T>>();

  // Optional configuration inputs
  multiple = input(false);
  clearable = input(true);
  searchable = input(true);
  placeholder = input('Select items...');
  hint = input<string>(''); // Helper text to show below the dropdown when empty
  initialIds = input<number[]>([]); // For loading initial selected items
  excludeIds = input<number[]>([]); // IDs to exclude from selection (already selected items)
  pageSize = input(20);
  searchDebounceTime = input(400); // Configurable search debounce time
  scrollDebounceTime = input(200); // Configurable scroll debounce time
  duplicateMessage = input('Item is already selected'); // Customizable duplicate message
  showDuplicateNotification = input(true); // Whether to show duplicate notifications
  clearOnSelection = input(false); // Whether to clear the dropdown after selection (for external list usage)
  searchField = input('name'); // Field to search on (default: name)
  showCustomTemplate = input(false); // Whether to show custom item template
  idsFilterKey = input<string>(''); // Filter key for fetching by IDs (e.g., 'CustomerIds', 'ProductIds')

  // Signal-based output (Angular 19)
  selectionChange = output<LazyDropdownItem | LazyDropdownItem[] | null>();

  // Internal state
  items$ = new BehaviorSubject<LazyDropdownItem[]>([]);
  loading = false;
  searchTerm = '';
  currentPage = 1;
  hasMore = true;
  value: any = this.multiple() ? [] : null;

  private destroy$ = new Subject<void>();
  searchSubject = new Subject<string>(); // Made public for template access
  private scrollSubject = new Subject<void>();
  private fetchSubject = new Subject<{ page: number; searchTerm: string }>();
  private refreshSubject = new Subject<void>();
  private currentRequest$ = new Subject<void>(); // For cancelling ongoing requests

  ngOnInit() {
    this.setupSearch();
    this.setupScrollPagination();
    this.setupFetchDebouncing();
    this.setupRefreshDebouncing();
    this.loadInitialData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch() {
    this.searchSubject
      .pipe(
        debounceTime(this.searchDebounceTime()), // Use configurable debounce time
        distinctUntilChanged(),
        tap((term: string) => {
          this.searchTerm = term;
          this.currentPage = 1;
          this.hasMore = true;
          this.items$.next([]);
        }),
        switchMap(term => this.debouncedFetchItems(1, term)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private setupScrollPagination() {
    this.scrollSubject
      .pipe(
        debounceTime(this.scrollDebounceTime()), // Use configurable debounce time
        distinctUntilChanged(),
        switchMap(() => {
          if (this.hasMore && !this.loading) {
            this.currentPage++;
            return this.debouncedFetchItems(this.currentPage, this.searchTerm);
          }
          return of([]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private setupFetchDebouncing() {
    this.fetchSubject
      .pipe(
        debounceTime(150), // Debounce all fetch operations
        distinctUntilChanged((prev, curr) =>
          prev.page === curr.page && prev.searchTerm === curr.searchTerm
        ),
        switchMap(({ page, searchTerm }) => this.fetchItems(page, searchTerm)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private setupRefreshDebouncing() {
    this.refreshSubject
      .pipe(
        debounceTime(300), // Debounce refresh operations
        switchMap(() => {
          this.currentPage = 1;
          this.hasMore = true;
          this.searchTerm = '';
          this.items$.next([]);
          return this.fetchItems(1, '');
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private loadInitialData() {
    // Load initial selected items first if provided
    const initialIds = this.initialIds();
    if (initialIds.length > 0 && this.idsFilterKey()) {
      // Fetch initial selected items using the same API adapter with ID filter
      this.fetchItemsByIds(initialIds).subscribe(items => {
        // Add initial items to the items list so they can be displayed properly
        const currentItems = this.items$.value;
        const uniqueItems = this.removeDuplicates([...items, ...currentItems]);
        this.items$.next(uniqueItems);

        // Set the selected values
        if (this.multiple()) {
          this.value = items.map(item => item.id);
        } else {
          this.value = items[0]?.id || null;
        }
      });
    } else {
      // Only load initial items if we have initial IDs to load
      // This prevents unnecessary API calls when the dropdown is empty
      this.items$.next([]);
    }
  }

  // Debounced version of fetchItems
  private debouncedFetchItems(page: number, searchTerm: string = ''): Observable<LazyDropdownItem[]> {
    // For immediate calls (like initial load), use direct fetch
    // For user-triggered calls, they go through the debounced subjects
    return this.fetchItems(page, searchTerm);
  }

  private fetchItems(page: number, searchTerm: string = ''): Observable<LazyDropdownItem[]> {
    // Prevent multiple simultaneous requests
    if (this.loading) {
      return of([]);
    }

    this.loading = true;

    const request: DataGetRequest = {
      pagination: {
        size: this.pageSize(),
        page: page
      }
    };

    // Add search filter if search term is provided
    if (searchTerm && searchTerm.trim()) {
      request.filters = [{
        key: this.searchField(),
        value: searchTerm.trim(),
        type: 'contains'
      }];
    }

    // Cancel any ongoing request
    this.currentRequest$.next();

    return this.apiAdapter()(request)
      .pipe(
        debounceTime(200), // Small debounce to prevent rapid API calls
        takeUntil(this.currentRequest$), // Cancel if new request starts
        takeUntil(this.destroy$), // Cancel on component destroy
        map(response => {
          let rawItems: T[] = [];
          let hasMore = false;

          // Handle array responses (most common case)
          if (Array.isArray(response)) {
            rawItems = response;
            hasMore = response.length === this.pageSize(); // Assume more if we got a full page
          } else {
            // Handle DataGetResponse format from GetApiAdapter
            rawItems = (response as any).data || [];
            const pagination = (response as any).pagination;
            if (pagination) {
              hasMore = pagination.page < pagination.totalPages;
            } else {
              hasMore = rawItems.length === this.pageSize();
            }
          }

          // Map raw items to LazyDropdownItem and filter out excluded items
          const excludeIds = this.excludeIds();
          const items: LazyDropdownItem[] = rawItems
            .filter((item: any) => !excludeIds.includes(item.id))
            .map(item => this.itemMapper()(item));

          this.hasMore = hasMore;

          if (page === 1) {
            // First page or new search - replace items
            this.items$.next(items);
          } else {
            // Subsequent pages - append items
            const currentItems = this.items$.value;
            const uniqueItems = this.removeDuplicates([...currentItems, ...items]);
            this.items$.next(uniqueItems);
          }

          return items;
        }),
        catchError(error => {
          console.error('Error fetching items:', error);
          this.hasMore = false;
          return of([]);
        }),
        finalize(() => {
          // Always reset loading state, even if request was cancelled
          this.loading = false;
        })
      );
  }

  private fetchItemsByIds(ids: number[]): Observable<LazyDropdownItem[]> {
    if (ids.length === 0 || !this.idsFilterKey()) {
      return of([]);
    }

    // Use the same API adapter with ID filter and proper pagination
    const request: DataGetRequest = {
      pagination: {
        page: 1,
        size: ids.length // Set page size to number of IDs to fetch all in one request
      },
      filters: [{ key: this.idsFilterKey(), value: ids }],
    };

    return this.apiAdapter()(request)
      .pipe(
        debounceTime(100), // Debounce ID-based fetching
        map(response => {
          let rawItems: T[] = [];

          // Handle array responses (most common case)
          if (Array.isArray(response)) {
            rawItems = response;
          } else {
            // Handle DataGetResponse format from GetApiAdapter
            rawItems = (response as any).data || [];
          }

          // Map raw items to LazyDropdownItem
          return rawItems.map(item => this.itemMapper()(item));
        }),
        catchError(error => {
          console.error('Error fetching items by IDs:', error);
          return of([]);
        })
      );
  }

  private removeDuplicates(items: LazyDropdownItem[]): LazyDropdownItem[] {
    const seen = new Set();
    return items.filter(item => {
      const key = item.id;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  onDropdownOpen() {
    // Optionally refresh data when dropdown opens with debouncing
    if (this.items$.value.length === 0) {
      this.debouncedFetchItems(1, this.searchTerm).subscribe();
    }
  }

  onSearch(searchTerm: string) {
    // For single selection, clear the current selection when user starts typing
    if (!this.multiple() && searchTerm && searchTerm.length > 0 && this.value) {
      this.value = null;
      this.selectionChange.emit(null);
    }

    this.searchSubject.next(searchTerm || '');
  }

  onScroll(event: any) {
    // Check if scrolled to bottom (with some threshold)
    const { end } = event;
    const threshold = 5; // Load more when 5 items from bottom

    if (end <= threshold && this.hasMore && !this.loading) {
      this.scrollSubject.next();
    }
  }

  onKeyDown(event: any) {
    // For single selection, clear the current selection when user presses backspace or starts typing
    if (!this.multiple() && this.value) {
      if (event.key === 'Backspace' || event.key === 'Delete') {
        this.value = null;
        this.selectionChange.emit(null);
        this.cdr.detectChanges();
      }
    }
  }

  onSelectionChange(selectedValue: any) {
    if (!selectedValue) {
      this.value = this.multiple() ? [] : null;
      this.selectionChange.emit(this.multiple() ? [] : null);
      return;
    }

    // Check for duplicates before processing selection
    const excludeIds = this.excludeIds();

    if (this.multiple()) {
      // For multiple selection, filter out excluded IDs
      const validIds = selectedValue.filter((id: number) => !excludeIds.includes(id));
      const duplicateIds = selectedValue.filter((id: number) => excludeIds.includes(id));

      if (duplicateIds.length > 0 && this.showDuplicateNotification()) {
        snackbar$.show(this.duplicateMessage());
      }

      this.value = validIds;
      const selectedItems = this.items$.value.filter(item => validIds.includes(item.id));
      this.selectionChange.emit(selectedItems);
    } else {
      // For single selection, check if the selected item is excluded
      if (excludeIds.includes(selectedValue)) {
        if (this.showDuplicateNotification()) {
          snackbar$.show(this.duplicateMessage());
        }
        // Clear the selection and don't emit
        this.clearSelection();
        return;
      }

      const selectedItem = this.items$.value.find(item => item.id === selectedValue);

      // Emit the selection first
      this.selectionChange.emit(selectedItem || null);

      // If clearOnSelection is true, clear the dropdown after emitting the selection
      if (this.clearOnSelection()) {
        // Use setTimeout to ensure the clearing happens after the current change detection cycle
        setTimeout(() => {
          this.value = null;
          if (this.ngSelect) {
            this.ngSelect.clearModel();
          }
          this.cdr.detectChanges();
        }, 0);
      } else {
        this.value = selectedValue;
      }
    }
  }

  trackByFn(item: LazyDropdownItem) {
    return item.id;
  }

  // Custom search function that disables client-side filtering
  // Always returns true so ng-select shows all items returned by the backend
  customSearchFn(_term: string, _item: LazyDropdownItem) {
    return true;
  }

  // Helper method to determine when to show hint
  shouldShowHint(): boolean {
    const result = this.multiple()
      ? (!this.value || (Array.isArray(this.value) && this.value.length === 0))
      : !this.value;

    return result;
  }

  // Public methods for external control
  clearSelection() {
    this.value = this.multiple() ? [] : null;
    if (this.ngSelect) {
      this.ngSelect.clearModel();
    }
    this.selectionChange.emit(this.multiple() ? [] : null);
    this.cdr.detectChanges();
  }

  refresh() {
    // Use debounced refresh
    this.refreshSubject.next();
  }
}